import {backgroundProcess} from '@cl/shared';
import * as authService from 'common/src/modules/auth/auth.service';
import {AppRoute, RouteConfig} from 'common/src/modules/routing/routing.types';
import {DateTime} from 'luxon';
import {jobsApi} from '../jobs/jobs.api';
import {JobUIStatus} from '../jobs/jobs.types';
import getJobLink from '../jobs/utils/getJobLink';
import CLLocalStorage from '../localStorage/CLLocalStorage';
import {
  BGProcess,
  BGProcessError,
  BGProcessLink,
  BGProcessLinkType,
  BGProcessStatus,
  ProcessType,
  RunningBGProcess,
  StartUiProcessParams,
} from './backgroundProcess.types';
import {LS_KEY__BG_PROCESSES} from '../localStorage/CLLocalStorage.consts';

const {savedProcesses, processes, pollingIntervalRef} = backgroundProcess;

const FETCH_REMOTE_JOBS_INTERVAL_MS = 5000;

function getRunningProcesses() {
  return processes.data.filter((process) => process.status === BGProcessStatus.InProgress);
}

function startProcess(
  name = '',
  id = Math.random().toString(),
  type = ProcessType.REMOTE,
): RunningBGProcess {
  const process: BGProcess = {
    id,
    name,
    status: BGProcessStatus.InProgress,
    percentComplete: 0,
    warnings: null,
    errors: null,
    startTime: DateTime.now().toMillis(),
    endTime: null,
    clientId: authService.getUserAuth().clientId,
    type,
  };

  processes.data = [...processes.data, process];

  return {
    ...process,
    success() {
      updateProcess(id, {
        status: BGProcessStatus.Success,
        endTime: DateTime.now().toMillis(),
      });
    },
    fail(e: BGProcessError) {
      const errors = !!e?.errors?.length ? e.errors : [e.message];

      updateProcess(id, {
        status: BGProcessStatus.Failed,
        errors,
        endTime: DateTime.now().toMillis(),
      });
    },
    warn(warnings: string[]) {
      updateProcess(id, {
        status: BGProcessStatus.Warn,
        endTime: DateTime.now().toMillis(),
        warnings,
      });
    },
    kill() {
      killProcess(id);
    },
    setLink({type, value: route, label, ...otherLinkProps}: BGProcessLink) {
      if (type === BGProcessLinkType.Href) {
        if (typeof route === 'string') route = {url: route} as AppRoute;
        (route as RouteConfig).accountId = process.clientId;
      }

      updateProcess(id, {
        link: {value: route, label, type, ...otherLinkProps} as BGProcessLink,
      });
    },
    setSubtitle(subtitle: string) {
      updateProcess(id, {subtitle});
    },
    setJobId(jobId: number) {
      updateProcess(id, {jobId});
    },
    progress(percentComplete: number) {
      updateProcess(id, {percentComplete});
    },
    setTitle(title: string) {
      updateProcess(id, {name: title});
    },
  };
}

function updateProcess(id: string, data: Partial<BGProcess>) {
  processes.data = processes.data.map((p) => (p.id === id ? {...p, ...data} : p));
}

function killProcess(id: string) {
  processes.data = processes.data.filter((p) => p.id !== id);
}

function clearAll() {
  processes.data = [];
}

function get() {
  return processes.data;
}

function getJobById(id: string) {
  return getRunningProcesses().find((job) => job.id === id);
}

processes.subscribe((data) => CLLocalStorage.set(LS_KEY__BG_PROCESSES, JSON.stringify(data)));

window.addEventListener('beforeunload', (e) => {
  if (
    processes.data.some(
      (process) =>
        process.status === BGProcessStatus.InProgress &&
        !process.jobId &&
        process.type !== ProcessType.PERSISTENT_LOCAL,
    )
  ) {
    return (e.returnValue =
      'Uploading files is in progress and will be canceled if you continue. Do you want to proceed?');
  }
});

function start() {
  const remoteRunningProcesses = savedProcesses.filter(
    (process) =>
      process.status === BGProcessStatus.InProgress &&
      !process.jobId &&
      process.type !== ProcessType.PERSISTENT_LOCAL,
  );

  const localPersistentRunningProcesses = savedProcesses.filter(
    (process) =>
      process.type === ProcessType.PERSISTENT_LOCAL &&
      process.status === BGProcessStatus.InProgress,
  );

  remoteRunningProcesses.forEach((process) => {
    updateProcess(process.id, {
      status: BGProcessStatus.Failed,
      errors: ['Process terminated by user'],
      endTime: DateTime.now().toMillis(),
    });
  });

  localPersistentRunningProcesses.forEach((process) => killProcess(process.id));

  const jobsInProgress = savedProcesses.filter(
    (process) => process.status === BGProcessStatus.InProgress && process.jobId,
  );

  pollingIntervalRef.current ??= setInterval(async () => {
    if (!jobsInProgress.length) {
      clearInterval(pollingIntervalRef.current);

      return;
    }

    jobsApi.getAllJobsStatus().then((jobsStatuses) => {
      for (const [idx, process] of jobsInProgress.entries()) {
        const job = jobsStatuses.find((status) => status.executionId === process.jobId);

        if (!job) {
          killProcess(process.id);
          continue;
        }

        switch (job?.status) {
          case JobUIStatus.Completed:
            updateProcess(process.id, {
              status: job.warnings?.length ? BGProcessStatus.Warn : BGProcessStatus.Success,
              warnings: job.warnings ?? [],
              endTime: DateTime.now().toMillis(),
              link: getJobLink(job.result),
            });
            jobsInProgress.splice(idx, 1);
            break;
          case JobUIStatus.Failed:
          case JobUIStatus.Aborted:
            updateProcess(process.id, {
              status: BGProcessStatus.Failed,
              endTime: DateTime.now().toMillis(),
              errors: job.errors?.length ? job.errors : [job.message],
            });
            jobsInProgress.splice(idx, 1);
            break;
        }
      }
    });
  }, FETCH_REMOTE_JOBS_INTERVAL_MS);
}

function stop() {
  clearInterval(pollingIntervalRef.current);
  pollingIntervalRef.current = null;
}

function startUIProcess<T>({
  processName,
  processFunc,
  type = ProcessType.LOCAL,
  id,
  starDate,
}: StartUiProcessParams<T>): Promise<T> {
  if (id && type === ProcessType.PERSISTENT_LOCAL) {
    const oldProcess = processes.data.find((p) => p.id === id);
    if (oldProcess) killProcess(oldProcess.id);
  }

  const bgProcess = startProcess(processName, id, type);

  if (starDate && id && type === ProcessType.PERSISTENT_LOCAL) {
    updateProcess(id, {startTime: starDate.toMillis()});
  }

  return processFunc(bgProcess)
    .then((result) => {
      bgProcess.success();

      return result;
    })
    .catch((e) => {
      bgProcess.fail(e);

      return Promise.reject(e);
    });
}

const backgroundProcessService = {
  startProcess,
  killProcess,
  clearAll,
  get,
  subscribe: processes.subscribe,
  unsubscribe: processes.unsubscribe,
  getJobById,
  start,
  stop,
  startUIProcess,
  updateProcess,
};

export {backgroundProcessService};
